import { AssessmentDefinition, CalculationType, InputType, SideType } from '../models/assessment-definition.model';

export const BBSDefinition: AssessmentDefinition = {
  id: 'bbs',
  version: '1.0',
  name: 'Berg Balance Skala',
  shortName: 'BBS',
  description: 'Die Berg Balance Skala ist ein Test zur Bewertung des Gleichgewichts bei älteren Erwachsenen.',
  guide:
    '<p>Die Berg Balance Skala bewertet das statische und dynamische Gleichgewicht anhand von 14 funktionellen Aufgaben.</p><p>Jede Aufgabe wird auf einer Skala von 0 bis 4 bewertet, wobei 0 für keine und 4 für eine vollständige Funktion steht. Die maximal erreichbare Punktzahl beträgt 56 Punkte.</p><p><strong>Interpretation des Gesamtscores:</strong><br>0-20 Punkte: hohes Sturzrisiko, Rollstuhlpflichtigkeit<br>21-40 Punkte: mittleres Sturzrisiko, Gehen mit Hilfe möglich<br>41-56 Punkte: niedriges Sturzrisiko, selbständig mobil</p>',
  groups: [
    {
      id: 'bbs-tasks',
      title: 'Balance Aufgaben',
      description: 'Die 14 Aufgaben der Berg Balance Skala',
      tasks: [
        {
          id: 'bbs-task-1',
          title: '1. Sitz zum Stand',
          description: 'Von der Sitzposition zum Stehen wechseln.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte stehen Sie auf und versuchen Sie, die Hände nicht zu benutzen.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann aufstehen ohne die Hände einzusetzen und sich selbstständig stabilisieren<br>3: kann selbstständig mit Einsatz der Hände aufstehen<br>2: kann nach einigen Versuchen mit Einsatz der Hände aufstehen<br>1: braucht minimale Hilfe zum Aufstehen oder zum stabilisieren<br>0: braucht mäßige bis maximale Hilfe um aufzustehen</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'braucht mäßige bis maximale Hilfe um aufzustehen' },
            { value: 1, label: '1', description: 'braucht minimale Hilfe zum Aufstehen oder zum stabilisieren' },
            { value: 2, label: '2', description: 'kann nach einigen Versuchen mit Einsatz der Hände aufstehen' },
            { value: 3, label: '3', description: 'kann selbstständig mit Einsatz der Hände aufstehen' },
            {
              value: 4,
              label: '4',
              description: 'kann aufstehen ohne die Hände einzusetzen und sich selbstständig stabilisieren',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true, // Sortierung von 4-0
          icfCategories: [{ code: 'd410', description: 'Eine elementare Körperposition wechseln' }],
        },
        {
          id: 'bbs-task-2',
          title: '2. Stehen ohne Unterstützung',
          description: 'Versuchen Sie, 2 Minuten ohne Unterstützung zu stehen.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte stehen Sie für 2 Minuten ohne sich festzuhalten.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann 2 Min. sicher stehen<br>3: kann 2 Min. unter Supervision stehen<br>2: kann 30 Sek. ohne Unterstützung stehen<br>1: braucht einige Versuche, um 30 Sek. Ohne Unterstützung zu Stehen<br>0: kann nicht ohne Unterstützung 30 Sek. stehen</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'kann nicht ohne Unterstützung 30 Sek. stehen' },
            {
              value: 1,
              label: '1',
              description: 'braucht einige Versuche, um 30 Sek. Ohne Unterstützung zu Stehen',
            },
            { value: 2, label: '2', description: 'kann 30 Sek. ohne Unterstützung stehen' },
            { value: 3, label: '3', description: 'kann 2 Min. unter Supervision stehen' },
            { value: 4, label: '4', description: 'kann 2 Min. sicher stehen' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4154', description: 'In stehender Position verbleiben' }],
        },
        {
          id: 'bbs-task-3',
          title: '3. Sitzen ohne Rückenlehne, beide Füße auf dem Boden',
          description: 'Sitzen Sie 2 Minuten mit verschränkten Armen.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte setzen Sie sich mit verschränkten Armen für 2 Minuten hin.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann sicher und stabil 2 Min. sitzen<br>3: kann 2 Min. unter Supervision sitzen<br>2: kann 30 Sek. sitzen<br>1: kann 10 Sek. sitzen<br>0: kann nicht ohne Unterstützung 10 Sek. sitzen</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'kann nicht ohne Unterstützung 10 Sek. sitzen' },
            { value: 1, label: '1', description: 'kann 10 Sek. sitzen' },
            { value: 2, label: '2', description: 'kann 30 Sek. sitzen' },
            { value: 3, label: '3', description: 'kann 2 Min. unter Supervision sitzen' },
            { value: 4, label: '4', description: 'kann sicher und stabil 2 Min. sitzen' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4153', description: 'In sitzender Position verbleiben' }],
        },
        {
          id: 'bbs-task-4',
          title: '4. Vom Stehen zum Sitzen',
          description: 'Setzen Sie sich hin.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte setzen Sie sich hin.</p><p><strong>Bewertungsschlüssel:</strong><br>4: setzt sich sicher mit minimalem Einsatz der Hände hin<br>3: kontrolliert das Hinsetzen mit den Händen<br>2: berührt mit Rückseite der Beine den Stuhl, um das Hinsetzen zu kontrollieren<br>1: setzt sich selbstständig hin, aber unkontrolliert<br>0: braucht Hilfe beim Hinsetzen</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'braucht Hilfe beim Hinsetzen' },
            { value: 1, label: '1', description: 'setzt sich selbstständig hin, aber unkontrolliert' },
            {
              value: 2,
              label: '2',
              description: 'berührt mit Rückseite der Beine den Stuhl, um das Hinsetzen zu kontrollieren',
            },
            { value: 3, label: '3', description: 'kontrolliert das Hinsetzen mit den Händen' },
            { value: 4, label: '4', description: 'setzt sich sicher mit minimalem Einsatz der Hände hin' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd410', description: 'Eine elementare Körperposition wechseln' }],
        },
        {
          id: 'bbs-task-5',
          title: '5. Transfer',
          description: 'Wechseln Sie vom Stuhl zum Bett und zurück.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte wechseln Sie vom Stuhl auf einen anderen Stuhl/Bett und zurück.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann den Transfer sicher mit minimalem Einsatz der Hände auszuführen<br>3: kann den Transfer sicher ausführen, muss aber die Hände einsetzen<br>2: kann den Transfer mit verbaler Anweisung und/oder unter Supervision ausführen<br>1: braucht 1 Person zur Hilfestellung<br>0: braucht 2 Personen zur Hilfestellung oder Supervision um sicher zu sein</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'braucht 2 Personen zur Hilfestellung oder Supervision um sicher zu sein',
            },
            { value: 1, label: '1', description: 'braucht 1 Person zur Hilfestellung' },
            {
              value: 2,
              label: '2',
              description: 'kann den Transfer mit verbaler Anweisung und/oder unter Supervision ausführen',
            },
            {
              value: 3,
              label: '3',
              description: 'kann den Transfer sicher ausführen, muss aber die Hände einsetzen',
            },
            {
              value: 4,
              label: '4',
              description: 'kann den Transfer sicher mit minimalem Einsatz der Hände auszuführen',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd420', description: 'Sich verlagern' }],
        },
        {
          id: 'bbs-task-6',
          title: '6. Stehen mit geschlossenen Augen ohne Unterstützung',
          description: 'Schließen Sie die Augen und bleiben Sie 10 Sekunden stehen.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte schließen Sie die Augen und bleiben Sie 10 Sekunden stehen.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann 10 Sek. sicher stehen<br>3: kann 10 Sek. unter Supervision stehen<br>2: kann 3 Sek. stehen<br>1: kann nicht die Augen 3 Sek. geschlossen halten, steht aber stabil<br>0: braucht Hilfe, um nicht zu fallen</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'braucht Hilfe, um nicht zu fallen' },
            {
              value: 1,
              label: '1',
              description: 'kann nicht die Augen 3 Sek. geschlossen halten, steht aber stabil',
            },
            { value: 2, label: '2', description: 'kann 3 Sek. stehen' },
            { value: 3, label: '3', description: 'kann 10 Sek. unter Supervision stehen' },
            { value: 4, label: '4', description: 'kann 10 Sek. sicher stehen' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4154', description: 'In stehender Position verbleiben' }],
        },
        {
          id: 'bbs-task-7',
          title: '7. Stehen ohne Unterstützung mit geschlossenen Füßen',
          description: 'Stellen Sie die Füße dicht zusammen und stehen Sie ohne Unterstützung.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte stellen Sie die Füße dicht zusammen und stehen Sie ohne Unterstützung.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann selbstständig Füße nebeneinander stellen und 1 Min. sicher stehen<br>3: kann selbstständig Füße nebeneinander stellen und unter Supervision 1 Min. stehen<br>2: kann selbstständig Füße nebeneinander stellen und die Position 30 Sek. halten<br>1: braucht Hilfe um Position einzunehmen, kann aber 15 Sek. mit geschlossenen Füßen stehen<br>0: braucht Hilfe um die Position einzunehmen, kann diese nicht für 15 Sek. halten</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'braucht Hilfe um die Position einzunehmen, kann diese nicht für 15 Sek. halten',
            },
            {
              value: 1,
              label: '1',
              description: 'braucht Hilfe um Position einzunehmen, kann aber 15 Sek. mit geschlossenen Füßen stehen',
            },
            {
              value: 2,
              label: '2',
              description: 'kann selbstständig Füße nebeneinander stellen und die Position 30 Sek. halten',
            },
            {
              value: 3,
              label: '3',
              description: 'kann selbstständig Füße nebeneinander stellen und unter Supervision 1 Min. stehen',
            },
            {
              value: 4,
              label: '4',
              description: 'kann selbstständig Füße nebeneinander stellen und 1 Min. sicher stehen',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4154', description: 'In stehender Position verbleiben' }],
        },
        {
          id: 'bbs-task-8',
          title: '8. Im Stehen mit ausgestreckten Armen nach vorne reichen/langen',
          description:
            'Heben Sie den Arm auf 90°. Strecken Sie die Finger aus und reichen Sie so weit wie möglich nach vorne.',
          guide:
            '<p><strong>Anleitung:</strong> Heben Sie den Arm auf 90°. Strecken Sie die Finger aus und reichen Sie so weit wie möglich nach vorne.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann sicher mehr als 25 cm nach vorne langen/reichen<br>3: kann sicher mehr als 12,5 cm nach vorne reichen/langen<br>2: kann sicher mehr als 5 cm nach vorne reichen<br>1: reicht nach vorne braucht aber Supervision<br>0: verliert das Gleichgewicht beim Versuch/braucht externe Unterstützung</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'verliert das Gleichgewicht beim Versuch/braucht externe Unterstützung',
            },
            { value: 1, label: '1', description: 'reicht nach vorne braucht aber Supervision' },
            { value: 2, label: '2', description: 'kann sicher mehr als 5 cm nach vorne reichen' },
            { value: 3, label: '3', description: 'kann sicher mehr als 12,5 cm nach vorne reichen/langen' },
            { value: 4, label: '4', description: 'kann sicher mehr als 25 cm nach vorne langen/reichen' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4106', description: 'Seinen Körperschwerpunkt verlagern' }],
        },
        {
          id: 'bbs-task-9',
          title: '9. Aus dem Stand Gegenstand vom Boden aufheben',
          description: 'Heben Sie einen Schuh/Hausschuh auf, der vor Ihren Füßen liegt.',
          guide:
            '<p><strong>Anleitung:</strong> Bitte heben Sie den Schuh/Hausschuh auf, der vor Ihren Füßen liegt.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann den Schuh sicher und mit Leichtigkeit aufheben<br>3: kann den Schuh aufheben, braucht aber Supervision<br>2: kann den Schuh nicht aufheben, reicht aber bis auf 2–5 cm an den Schuh heran und hält selbstständig das Gleichgewicht<br>1: kann den Schuh nicht aufheben und braucht bei dem Versuch Supervision<br>0: schon der Versuch scheitert oder braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description:
                'schon der Versuch scheitert oder braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen',
            },
            {
              value: 1,
              label: '1',
              description: 'kann den Schuh nicht aufheben und braucht bei dem Versuch Supervision',
            },
            {
              value: 2,
              label: '2',
              description:
                'kann den Schuh nicht aufheben, reicht aber bis auf 2–5 cm an den Schuh heran und hält selbstständig das Gleichgewicht',
            },
            { value: 3, label: '3', description: 'kann den Schuh aufheben, braucht aber Supervision' },
            { value: 4, label: '4', description: 'kann den Schuh sicher und mit Leichtigkeit aufheben' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [
            { code: 'd4105', description: 'Sich beugen' },
            { code: 'd4400', description: 'Einen Gegenstand aufheben' },
          ],
        },
        {
          id: 'bbs-task-10',
          title: '10. Sicher im Stehen umdrehen, um nach hinten über die rechte und die linke Schulter zu schauen',
          description: 'Drehen Sie sich nach links und schauen Sie hinter sich. Wiederholen Sie dies nach rechts.',
          guide:
            '<p><strong>Anleitung:</strong> Schauen Sie direkt hinter sich über die linke Schulter. Wiederholen Sie dies zur rechten Seite.</p><p><strong>Bewertungsschlüssel:</strong><br>4: schaut hinter sich über beide Seiten bei guter Gewichtsverlagerung<br>3: schaut nur über eine Seite nach hinten, und zeigt weniger Gewichtsverlagerung auf der anderen Seite<br>2: dreht sich nur zur Seite aber bewahrt das Gleichgewicht<br>1: braucht Supervision beim Umdrehen<br>0: braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen',
            },
            { value: 1, label: '1', description: 'braucht Supervision beim Umdrehen' },
            { value: 2, label: '2', description: 'dreht sich nur zur Seite aber bewahrt das Gleichgewicht' },
            {
              value: 3,
              label: '3',
              description:
                'schaut nur über eine Seite nach hinten, und zeigt weniger Gewichtsverlagerung auf der anderen Seite',
            },
            {
              value: 4,
              label: '4',
              description: 'schaut hinter sich über beide Seiten bei guter Gewichtsverlagerung',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [
            { code: 'd4154', description: 'In stehender Position verbleiben' },
            { code: 'd4106', description: 'Seinen Körperschwerpunkt verlagern' },
          ],
        },
        {
          id: 'bbs-task-11',
          title: '11. Sich um 360° drehen',
          description: 'Drehen Sie sich vollständig im Kreis. Halten Sie an. Drehen Sie sich in die andere Richtung.',
          guide:
            '<p><strong>Anleitung:</strong> Drehen Sie sich vollständig im Kreis. Halten Sie an. Drehen Sie sich dann in die andere Richtung.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann sich um 360° in 4 Sek. oder weniger drehen<br>3: kann sich nur in einer Richtung sicher um 360° in 4 Sek. oder weniger drehen<br>2: kann sich sicher um 360° drehen, aber langsam<br>1: braucht nahe Supervision oder verbale Hilfestellung<br>0: braucht Hilfe beim Drehen</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'braucht Hilfe beim Drehen' },
            { value: 1, label: '1', description: 'braucht nahe Supervision oder verbale Hilfestellung' },
            { value: 2, label: '2', description: 'kann sich sicher um 360° drehen, aber langsam' },
            {
              value: 3,
              label: '3',
              description: 'kann sich nur in einer Richtung sicher um 360° in 4 Sek. oder weniger drehen',
            },
            { value: 4, label: '4', description: 'kann sich um 360° in 4 Sek. oder weniger drehen' },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4106', description: 'Seinen Körperschwerpunkt verlagern' }],
        },
        {
          id: 'bbs-task-12',
          title: '12. Ohne Unterstützung abwechselnd die Füße auf eine Stufe stellen',
          description:
            'Stellen Sie abwechselnd jeden Fuß auf eine Stufe/Bank. Führen Sie dies fort, bis jeder Fuß 4-mal die Stufe berührt hat.',
          guide:
            '<p><strong>Anleitung:</strong> Stellen Sie abwechselnd jeden Fuß auf die Stufe/Bank. Fahren Sie fort, bis jeder Fuß 4-mal aufgesetzt hat.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann sicher und selbstständig stehen und innerhalb von 20 Sek. die 8 Schrittfolgen bzw. -stufen stellen<br>3: kann sicher selbstständig stehen und in mehr als 20 Sek. die 8 Schrittfolgen bzw. -Stufen absolvieren<br>2: kann 4 Schrittfolgen bzw. -stufen ohne Hilfe unter Supervision<br>1: kann mehr als 2 Schrittfolgen bzw. -stufen mit minimaler Hilfe absolvieren<br>0: braucht Hilfe um nicht zu fallen oder schon der Versuch scheitert</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'braucht Hilfe um nicht zu fallen oder schon der Versuch scheitert',
            },
            {
              value: 1,
              label: '1',
              description: 'kann mehr als 2 Schrittfolgen bzw. -stufen mit minimaler Hilfe absolvieren',
            },
            { value: 2, label: '2', description: 'kann 4 Schrittfolgen bzw. -stufen ohne Hilfe unter Supervision' },
            {
              value: 3,
              label: '3',
              description:
                'kann sicher selbstständig stehen und in mehr als 20 Sek. die 8 Schrittfolgen bzw. -Stufen absolvieren',
            },
            {
              value: 4,
              label: '4',
              description:
                'kann sicher und selbstständig stehen und innerhalb von 20 Sek. die 8 Schrittfolgen bzw. -stufen stellen',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4106', description: 'Seinen Körperschwerpunkt verlagern' }],
        },
        {
          id: 'bbs-task-13',
          title: '13. Tandemstand: Stehen ohne Unterstützung mit einem Fuß vor dem anderen',
          description:
            'Stellen Sie einen Fuß direkt vor den anderen. Wenn Sie spüren, dass Sie den vorderen Fuß nicht direkt vor den anderen stellen können, versuchen Sie einen Schritt weit genug nach vorne zu machen, sodass die Ferse des vorderen Fußes vor den Zehen des hinteren Fußes ist.',
          guide:
            '<p><strong>Anleitung:</strong> Stellen Sie einen Fuß direkt vor den anderen (Tandemstand). Wenn Sie spüren, dass Sie das nicht können, versuchen Sie einen Schritt zu machen, so dass die Ferse des vorderen Fußes vor den Zehen des hinteren Fußes ist (Semi-Tandemstand).</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann selbstständig die Füße in den Tandemstand bringen und 30 Sek. Halten<br>3: kann selbstständig einen Fuß vor den anderen stellen und diese Position 30 Sek. halten<br>2: kann selbstständig einen kleinen Schritte nach vorne machen und diese Position 30 Sek. halten<br>1: braucht Hilfe für den Schritt, kann aber Position 15 Sek. beibehalten<br>0: verliert Gleichgewicht während des Schritts oder des Stehens</p>',
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'verliert Gleichgewicht während des Schritts oder des Stehens' },
            {
              value: 1,
              label: '1',
              description: 'braucht Hilfe für den Schritt, kann aber Position 15 Sek. beibehalten',
            },
            {
              value: 2,
              label: '2',
              description:
                'kann selbstständig einen kleinen Schritte nach vorne machen und diese Position 30 Sek. halten',
            },
            {
              value: 3,
              label: '3',
              description: 'kann selbstständig einen Fuß vor den anderen stellen und diese Position 30 Sek. halten',
            },
            {
              value: 4,
              label: '4',
              description: 'kann selbstständig die Füße in den Tandemstand bringen und 30 Sek. Halten',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [{ code: 'd4154', description: 'In stehender Position verbleiben' }],
        },
        {
          id: 'bbs-task-14',
          title: '14. Auf einem Bein stehen',
          description: 'Stehen Sie auf einem Bein, solange Sie können.',
          guide:
            '<p><strong>Anleitung:</strong> Stehen Sie auf einem Bein, solange Sie können, ohne sich festzuhalten.</p><p><strong>Bewertungsschlüssel:</strong><br>4: kann ein Bein selbstständig anheben und Position länger als 10 Sek. Halten<br>3: kann ein Bein selbstständig anheben und Position für 5–10 Sek. Halten<br>2: kann ein Bein selbstständig anheben und die Position 3 Sek. Oder länger halten<br>1: versucht ein Bein anzuheben, kann Position nicht 3 Sek. lang beibehalten, bleibt aber selbstständig stehen<br>0: schon der Versuch scheitert oder Proband braucht Hilfe, um nicht zu fallen</p>',
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'schon der Versuch scheitert oder Proband braucht Hilfe, um nicht zu fallen',
            },
            {
              value: 1,
              label: '1',
              description:
                'versucht ein Bein anzuheben, kann Position nicht 3 Sek. lang beibehalten, bleibt aber selbstständig stehen',
            },
            {
              value: 2,
              label: '2',
              description: 'kann ein Bein selbstständig anheben und die Position 3 Sek. Oder länger halten',
            },
            {
              value: 3,
              label: '3',
              description: 'kann ein Bein selbstständig anheben und Position für 5–10 Sek. Halten',
            },
            {
              value: 4,
              label: '4',
              description: 'kann ein Bein selbstständig anheben und Position länger als 10 Sek. Halten',
            },
          ],
          minValue: 0,
          maxValue: 4,
          defaultValue: null,
          isRequired: true,
          sortOptionsReversed: true,
          icfCategories: [
            { code: 'd4154', description: 'In stehender Position verbleiben' },
            { code: 'd4106', description: 'Seinen Körperschwerpunkt verlagern' },
          ],
        },
      ],
      // Wir entfernen die allgemeinen ICF-Kategorien auf Gruppenebene, da wir sie nun auf Task-Ebene haben
    },
  ],
  icfCategories: [
    { code: 'b755', description: 'Funktionen der unwillkürlichen Bewegungsreaktionen' },
    { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
  ],
  calculationType: CalculationType.Sum,
  showPercentFulfillment: true,
  showICFScore: true,
  requiresPatientData: false,
  sideType: SideType.None,
};
